/**
 * TextAreaExamples.tsx
 * Comprehensive examples of HeroUI TextArea component
 * 
 * Showcases all HeroUI TextArea features using our shared components
 */

import React, { useState } from 'react';
import { PlaygroundPageLayout } from '../shared';
import { TextArea } from '@enfomentis/ui';
import { Card, CardHeader, CardBody } from '@enfomentis/ui';
import { Badge } from '@enfomentis/ui';
import { Button } from '@enfomentis/ui';
import { 
  MessageSquare,
  FileText,
  Edit,
  Send,
  Zap
} from 'lucide-react';

export const TextAreaExamples: React.FC = () => {
  const [values, setValues] = useState<Record<string, string>>({
    basic: '',
    bordered: '',
    underlined: '',
    faded: '',
    flat: '',
    description: '',
    comments: '',
    notes: 'Initial content in textarea',
    feedback: '',
    disabled: 'Cannot edit this content',
    readonly: 'This is read-only content that cannot be modified',
    validated: '',
    small: '',
    medium: '',
    large: '',
    primary: '',
    secondary: '',
    success: '',
    warning: '',
    danger: '',
    autoResize: 'Type more content to see auto-resize functionality in action...',
    withContent: '',
    clearable: 'Some initial content that can be cleared'
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleValueChange = (key: string, value: string) => {
    setValues(prev => ({ ...prev, [key]: value }));
    
    // Clear error when user starts typing
    if (errors[key]) {
      setErrors(prev => ({ ...prev, [key]: '' }));
    }
  };

  const validateFeedback = (feedback: string): string => {
    if (feedback.length > 0 && feedback.length < 10) {
      return 'Feedback must be at least 10 characters';
    }
    if (feedback.length > 500) {
      return 'Feedback must be less than 500 characters';
    }
    return '';
  };

  const handleFeedbackValidation = () => {
    const error = validateFeedback(values['validated'] || '');
    setErrors(prev => ({ ...prev, validated: error }));
  };

  return (
    <PlaygroundPageLayout>
      <div className="space-y-8">
        
        {/* Header */}
        <div className="space-y-2">
          <h1 className="text-3xl font-bold text-text-primary">TextArea Examples</h1>
          <p className="text-text-secondary">
            Comprehensive showcase of HeroUI TextArea component with automatic theme integration and auto-resize functionality
          </p>
        </div>

        {/* Basic Variants */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">TextArea Variants</h2>
              <Badge color="primary" variant="flat" size="sm">4 variants</Badge>
            </div>
          </CardHeader>
          <CardBody className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <TextArea
                variant="flat"
                label="Flat Variant"
                placeholder="Enter your message..."
                value={values['flat'] || ''}
                onValueChange={(value: string) => handleValueChange('flat', value)}
                description="Flat textarea with subtle background"
                minRows={3}
              />
              
              <TextArea
                variant="bordered"
                label="Bordered Variant"
                placeholder="Enter your text..."
                value={values['bordered'] || ''}
                onValueChange={(value) => handleValueChange('bordered', value)}
                description="TextArea with visible border"
                minRows={3}
              />
              
              <TextArea
                variant="underlined"
                label="Underlined Variant"
                placeholder="Enter details..."
                value={values['underlined'] || ''}
                onValueChange={(value) => handleValueChange('underlined', value)}
                description="TextArea with bottom border only"
                minRows={3}
              />
              
              <TextArea
                variant="faded"
                label="Faded Variant"
                placeholder="Enter content..."
                value={values['faded'] || ''}
                onValueChange={(value) => handleValueChange('faded', value)}
                description="Faded appearance textarea"
                minRows={3}
              />
            </div>
          </CardBody>
        </Card>

        {/* Auto-resize and Content */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">Auto-resize & Content</h2>
              <Badge color="success" variant="flat" size="sm">Dynamic sizing</Badge>
            </div>
          </CardHeader>
          <CardBody className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <TextArea
                variant="bordered"
                label="Auto-resize TextArea"
                placeholder="Type multiple lines to see auto-resize..."
                value={values['autoResize'] || ''}
                onValueChange={(value) => handleValueChange('autoResize', value)}
                description="Automatically adjusts height (3-8 rows)"
                minRows={3}
                maxRows={8}
              />
              
              <TextArea
                variant="bordered"
                label="With Start Content"
                placeholder="Add your comments..."
                value={values['withContent'] || ''}
                onValueChange={(value) => handleValueChange('withContent', value)}
                startContent={<MessageSquare className="w-4 h-4 text-default-400" />}
                endContent={
                  <Button size="sm" variant="flat" className="mt-2">
                    <Send className="w-3 h-3" />
                  </Button>
                }
                minRows={4}
              />
            </div>
          </CardBody>
        </Card>

        {/* Sizes */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">TextArea Sizes</h2>
              <Badge color="warning" variant="flat" size="sm">3 sizes</Badge>
            </div>
          </CardHeader>
          <CardBody className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <TextArea
                size="sm"
                variant="bordered"
                label="Small"
                placeholder="Small textarea"
                value={values['small'] || ''}
                onValueChange={(value) => handleValueChange('small', value)}
                minRows={2}
              />
              
              <TextArea
                size="md"
                variant="bordered"
                label="Medium"
                placeholder="Medium textarea"
                value={values['medium'] || ''}
                onValueChange={(value) => handleValueChange('medium', value)}
                minRows={3}
              />
              
              <TextArea
                size="lg"
                variant="bordered"
                label="Large"
                placeholder="Large textarea"
                value={values['large'] || ''}
                onValueChange={(value) => handleValueChange('large', value)}
                minRows={4}
              />
            </div>
          </CardBody>
        </Card>

        {/* Colors */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">TextArea Colors</h2>
              <Badge color="danger" variant="flat" size="sm">Theme colors</Badge>
            </div>
          </CardHeader>
          <CardBody className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <TextArea
                color="primary"
                variant="bordered"
                label="Primary"
                placeholder="Primary color textarea"
                value={values['primary'] || ''}
                onValueChange={(value) => handleValueChange('primary', value)}
                minRows={3}
              />
              
              <TextArea
                color="secondary"
                variant="bordered"
                label="Secondary"
                placeholder="Secondary color textarea"
                value={values['secondary'] || ''}
                onValueChange={(value) => handleValueChange('secondary', value)}
                minRows={3}
              />
              
              <TextArea
                color="success"
                variant="bordered"
                label="Success"
                placeholder="Success color textarea"
                value={values['success'] || ''}
                onValueChange={(value) => handleValueChange('success', value)}
                minRows={3}
              />
              
              <TextArea
                color="warning"
                variant="bordered"
                label="Warning"
                placeholder="Warning color textarea"
                value={values['warning'] || ''}
                onValueChange={(value) => handleValueChange('warning', value)}
                minRows={3}
              />
              
              <TextArea
                color="danger"
                variant="bordered"
                label="Danger"
                placeholder="Danger color textarea"
                value={values['danger'] || ''}
                onValueChange={(value) => handleValueChange('danger', value)}
                minRows={3}
              />
            </div>
          </CardBody>
        </Card>

        {/* States & Features */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">States & Features</h2>
              <Badge variant="flat" size="sm">Interactive</Badge>
            </div>
          </CardHeader>
          <CardBody className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <TextArea
                variant="bordered"
                label="Clearable TextArea"
                placeholder="Type to see clear button"
                value={values['clearable'] || ''}
                onValueChange={(value) => handleValueChange('clearable', value)}
                isClearable
                description="Clear button appears when content exists"
                minRows={3}
              />
              
              <TextArea
                variant="bordered"
                label="Required Field"
                placeholder="This field is required"
                value={values['notes'] || ''}
                onValueChange={(value) => handleValueChange('notes', value)}
                isRequired
                color="primary"
                minRows={3}
              />
              
              <TextArea
                variant="bordered"
                label="Disabled TextArea"
                value={values['disabled'] || ''}
                onValueChange={(value) => handleValueChange('disabled', value)}
                isDisabled
                description="This textarea is disabled"
                minRows={3}
              />
              
              <TextArea
                variant="bordered"
                label="Read-only TextArea"
                value={values['readonly'] || ''}
                onValueChange={(value) => handleValueChange('readonly', value)}
                isReadOnly
                description="This textarea is read-only"
                minRows={3}
              />
            </div>
          </CardBody>
        </Card>

        {/* Validation Example */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">Validation Example</h2>
              <Badge color="danger" variant="flat" size="sm">Real-time</Badge>
            </div>
          </CardHeader>
          <CardBody>
            <div className="max-w-2xl">
              <TextArea
                variant="bordered"
                label="Feedback (10-500 characters)"
                placeholder="Enter your feedback..."
                value={values['validated'] || ''}
                onValueChange={(value) => handleValueChange('validated', value)}
                onBlur={handleFeedbackValidation}
                isInvalid={!!errors['validated']}
                errorMessage={errors['validated']}
                color={errors['validated'] ? "danger" : "default"}
                startContent={<Edit className="w-4 h-4 text-default-400" />}
                description={`${values['validated']?.length || 0}/500 characters. Validates on blur.`}
                minRows={4}
                maxRows={8}
              />
            </div>
          </CardBody>
        </Card>

        {/* Label Placements */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">Label Placements</h2>
              <Badge color="secondary" variant="flat" size="sm">4 positions</Badge>
            </div>
          </CardHeader>
          <CardBody className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <TextArea
                variant="bordered"
                label="Inside Label"
                placeholder="Type here..."
                labelPlacement="inside"
                value={values['description'] || ''}
                onValueChange={(value) => handleValueChange('description', value)}
                minRows={3}
              />
              
              <TextArea
                variant="bordered"
                label="Outside Label"
                placeholder="Type here..."
                labelPlacement="outside"
                value={values['comments'] || ''}
                onValueChange={(value) => handleValueChange('comments', value)}
                minRows={3}
              />
              
              <TextArea
                variant="bordered"
                label="Outside Left"
                placeholder="Type here..."
                labelPlacement="outside-left"
                value={values['feedback'] || ''}
                onValueChange={(value) => handleValueChange('feedback', value)}
                minRows={3}
              />
              
              <TextArea
                variant="bordered"
                label="Outside Top"
                placeholder="Type here..."
                labelPlacement="outside-top"
                value={values['basic'] || ''}
                onValueChange={(value) => handleValueChange('basic', value)}
                minRows={3}
              />
            </div>
          </CardBody>
        </Card>

        {/* Real-world Use Cases */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">Real-world Examples</h2>
              <Badge color="primary" variant="flat" size="sm">Practical</Badge>
            </div>
          </CardHeader>
          <CardBody className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <TextArea
                variant="bordered"
                label="Trade Notes"
                placeholder="Record your trading thoughts, market analysis, and strategy notes..."
                startContent={<FileText className="w-4 h-4 text-default-400" />}
                color="primary"
                minRows={5}
                maxRows={10}
                description="Document your trading analysis and decisions"
              />
              
              <TextArea
                variant="bordered"
                label="Quick Message"
                placeholder="Send a quick message..."
                startContent={<Zap className="w-4 h-4 text-default-400" />}
                endContent={
                  <div className="flex gap-2 mt-2">
                    <Button size="sm" variant="flat">
                      Save Draft
                    </Button>
                    <Button size="sm" color="primary">
                      <Send className="w-3 h-3 mr-1" />
                      Send
                    </Button>
                  </div>
                }
                color="secondary"
                minRows={4}
                maxRows={6}
                description="Compose and send messages quickly"
              />
            </div>
          </CardBody>
        </Card>

      </div>
    </PlaygroundPageLayout>
  );
};